import { useEffect, useRef, useState } from "react";

/**
 * Renders an admin-managed press asset, but never leaves a broken-image card
 * on the public EPK. If the managed object fails to load, `onFail` lets the
 * parent omit the block or swap in canonical artwork.
 *
 * The error can fire during SSR hydration, before React attaches `onError`,
 * so a mount-time check re-inspects the element's decoded size.
 */
export function ManagedAssetImage({
  src,
  alt,
  className,
  onFail,
  loading = "lazy",
}: {
  src: string;
  alt: string;
  className?: string;
  onFail?: () => void;
  loading?: "lazy" | "eager";
}) {
  const [failed, setFailed] = useState(false);
  const ref = useRef<HTMLImageElement>(null);

  const fail = () => {
    setFailed(true);
    onFail?.();
  };

  useEffect(() => {
    const el = ref.current;
    if (el && el.complete && el.naturalWidth === 0) fail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (failed) return null;
  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      loading={loading}
      decoding="async"
      className={className}
      onError={fail}
    />
  );
}
